import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './auth';
import { upsertById } from './trade';

// Everything the Leaflets page works from: the people, zones, jobs (with their
// rounds), payments and settings, in one request. Kept current from what each
// save sends back, and reloaded when Farm OS comes back into focus so rounds
// sent in from the app in the meantime turn up.
//
// status: 'loading' | 'ready' | 'missing' (the server has no /leaflets routes
// yet, i.e. garageFarmServer hasn't been deployed) | 'error'
export function useLeafletsData() {
  const [data, setData] = useState({ distributors: [], zones: [], jobs: [], payouts: [], settings: null });
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const loadedAt = useRef(0);

  const load = useCallback(async () => {
    try {
      const { data: all } = await api.get('/leaflets/admin/all');
      setData(all);
      setStatus('ready');
      loadedAt.current = Date.now();
    } catch (e) {
      if (e.response?.status === 404) return setStatus('missing');
      setError(e.response?.data?.error || e.message);
      setStatus(s => (s === 'ready' ? s : 'error'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onFocus = () => { if (Date.now() - loadedAt.current > 60000) load(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const put = useCallback((key, item) => setData(d => ({ ...d, [key]: upsertById(d[key], item) })), []);
  const drop = useCallback((key, id) => setData(d => ({ ...d, [key]: d[key].filter(x => x._id !== id) })), []);

  const putPerson = useCallback(p => put('distributors', p), [put]);
  const putZone   = useCallback(z => put('zones', z), [put]);
  const putJob    = useCallback(j => put('jobs', j), [put]);
  const putJobs   = useCallback(list => setData(d => ({ ...d, jobs: list.reduce(upsertById, d.jobs) })), []);
  const putPayout = useCallback(p => put('payouts', p), [put]);
  const setSettings = useCallback(settings => setData(d => ({ ...d, settings })), []);

  return {
    ...data, status, error, reload: load,
    putPerson, putZone, putJob, putJobs, putPayout, setSettings,
    dropPerson: id => drop('distributors', id),
    dropZone:   id => drop('zones', id),
    dropJob:    id => drop('jobs', id),
    dropPayout: id => drop('payouts', id),
  };
}
